import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useState, useEffect, useMemo} from 'react'
import Image from 'next/image'
import {clsx} from 'clsx'
import {useRouter} from 'next/router'
import type {Block, TersedAsset} from '@/services/kontent-ai/types'
import {Modal} from '@/components/modal/modal'
import {loadImageFromKontentAI} from '@/image-loader'
import {useAuth} from '@/products/auth/auth-provider'
import {getBrandColorClasses} from '@/products/pdp/utils/brand-colors'
import {RebatesRowBlock} from '@/products/your-pet-and-you/blocks/rebates-table-and-filter/rebates-table/rebates-row'
import type {RebatesTableRowContentItem} from '@/products/your-pet-and-you/blocks/rebates-table-and-filter/rebates-table'
import type {IconWithTextContentItem} from '@/products/flexible-web-toolkit/blocks/icon-with-text'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import type {SmallTextContentItem} from '../../flexible-web-toolkit/blocks/small-text'
import {ReusableCTAButtonComponent} from '../../flexible-web-toolkit/blocks/cta-button'
import type {CtaButtonContentItem} from '../../flexible-web-toolkit/blocks/cta-button'

export type PDPPopupRebatesContentItem = IContentItem<{
	buttonText: Elements.TextElement
	icon: Elements.AssetsElement
	title: Elements.TextElement
	termsAndConditionsText: Elements.TextElement
	termsAndConditionsUrl: Elements.TextElement
	rebatesTable: Elements.LinkedItemsElement<RebatesTableRowContentItem>
	loginTitle: Elements.TextElement
	iconWithText: Elements.LinkedItemsElement<IconWithTextContentItem>
	loginButton: Elements.LinkedItemsElement<CtaButtonContentItem>
	registerButton: Elements.LinkedItemsElement<CtaButtonContentItem>
	columnHeadings: Elements.LinkedItemsElement<SmallTextContentItem>
}>

export const PDPPopupRebatesBlock: Block<PDPPopupRebatesContentItem> = ({
	block,
	BlockMapper,
	globalConfig,
	page,
}) => {
	const authState = useAuth()
	const {authenticated} = authState

	const router = useRouter()

	const returnUrl = `/${router.locale}${router.asPath}`

	const [isModalOpen, setIsModalOpen] = useState(false)
	const {
		buttonText,
		icon,
		title,
		termsAndConditionsText,
		termsAndConditionsUrl,
		rebatesTable,
		loginTitle,
		iconWithText,
		loginButton,
		registerButton,
		columnHeadings,
	} = block.elements

	const iconAsset: TersedAsset | undefined = icon[0]
	const {brandColor, brandColorClasses} = useMemo(() => {
		const {colorId} = colorWithFallback({
			pageConfig: page.elements.config[0],
		})

		const classes = getBrandColorClasses(colorId)

		return {brandColor: colorId, brandColorClasses: classes}
	}, [page.elements.config])

	const backgroundBrandColor = brandColor ? `bg-${brandColor}` : 'bg-primary'

	const visibleLoginButton = loginButton.filter(
		(button) =>
			button.elements.hideComponentOnLoginEnabled?.[0]?.codename === 'yes'
	)
	const visibleRegisterButton = registerButton.filter(
		(button) =>
			button.elements.hideComponentOnLoginEnabled?.[0]?.codename === 'yes'
	)

	const handleOpenModal = (): void => {
		setIsModalOpen(true)
	}

	const handleCloseModal = (): void => {
		setIsModalOpen(false)
	}

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'Escape' && isModalOpen) {
				handleCloseModal()
			}
		}

		const handleClickOutside = (e: MouseEvent): void => {
			if (isModalOpen) {
				const target = e.target as HTMLElement
				if (target.classList.contains('bg-black/50')) {
					handleCloseModal()
				}
			}
		}

		if (isModalOpen) {
			document.addEventListener('keydown', handleKeyDown)
			document.addEventListener('click', handleClickOutside)
			return () => {
				document.removeEventListener('keydown', handleKeyDown)
				document.removeEventListener('click', handleClickOutside)
			}
		}
	}, [isModalOpen])

	return (
		<div className="inline-block">
			<button
				aria-expanded={isModalOpen}
				aria-haspopup="dialog"
				className={`inline-flex items-center gap-2 rounded border-2 px-3 py-1 text-sm font-bold transition-all duration-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
					brandColorClasses.border
				} bg-white ${
					brandColorClasses.text
				} hover:bg-gray-50 focus:ring-${brandColor || 'elanco-blue'}`}
				onClick={handleOpenModal}
				type="button"
			>
				{iconAsset ? (
					<Image
						alt=""
						className="h-5 w-5 flex-shrink-0"
						height={20}
						loader={loadImageFromKontentAI}
						src={iconAsset.url}
						width={20}
					/>
				) : null}
				{buttonText}
			</button>

			{isModalOpen ? (
				<Modal
					brandColor={brandColor || 'elanco-blue'}
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					size="lg"
					title={title}
					variant="right-slide"
				>
					<div className="space-y-3">
						{rebatesTable.length > 0 ? (
							<div className="space-y-4">
								<div className="block w-full">
									<table className="block w-full">
										<tbody className="block border-t border-light-grey">
											{rebatesTable.map((rebate) => (
												<RebatesRowBlock
													BlockMapper={BlockMapper}
													block={{
														elements: {
															columnHeadings,
															rebate: [rebate],
														},
														system: rebate.system,
													}}
													forceMobileView
													globalConfig={globalConfig}
													hideClaimButtons
													key={rebate.system.id}
													page={page}
												/>
											))}
										</tbody>
									</table>
								</div>
							</div>
						) : null}

						{/* Terms & Conditions with Separator Line */}
						{termsAndConditionsText && termsAndConditionsUrl ? (
							<div className="300 pb-2">
								<div className="text-right md:text-left">
									<a
										className="text-[14px] font-bold leading-none underline"
										href={termsAndConditionsUrl}
										rel="noopener noreferrer"
										target="_blank"
									>
										{termsAndConditionsText}
									</a>
								</div>
							</div>
						) : null}
						<div className="space-y-[13px]">
							{!authenticated && loginTitle ? (
								<h3 className="text-gray-900 py-4 text-[24px] font-bold leading-none">
									{loginTitle}
								</h3>
							) : null}
						</div>
						{!authenticated && iconWithText.length > 0 ? (
							<div className="space-y-[13px]">
								{iconWithText.map(
									({
										elements: {
											icon: itemIcon,
											title: itemTitle,
											boldTitle,
										},
										system: {id},
									}) => (
										<div
											className="flex items-start space-x-3"
											key={id}
										>
											{itemIcon[0]?.url ? (
												<div className="flex-shrink-0">
													<Image
														alt={
															itemIcon[0]
																?.description ??
															''
														}
														height={24}
														loader={
															loadImageFromKontentAI
														}
														src={itemIcon[0].url}
														width={24}
													/>
												</div>
											) : null}
											<div className="flex-1">
												{itemTitle ? (
													<span
														className={clsx(
															'text-gray-900',
															boldTitle[0]
																?.codename ===
																'bold'
																? 'font-bold'
																: 'font-normal'
														)}
													>
														{itemTitle}
													</span>
												) : null}
											</div>
										</div>
									)
								)}
							</div>
						) : null}
						{!authenticated ? (
							<div className="space-y-[13px]">
								<div className="flex flex-wrap">
									{visibleLoginButton.map((button, i) => (
										<div
											className={clsx(
												'flex',
												visibleLoginButton.length +
													visibleRegisterButton.length >=
													2
													? 'w-1/2'
													: 'w-fit'
											)}
											key={button.system.id}
										>
											<div
												className={clsx(
													'mt-6 flex flex-1 flex-col justify-center',
													i === 0 ? 'mr-1' : 'ml-1'
												)}
											>
												<ReusableCTAButtonComponent
													BlockMapper={BlockMapper}
													block={{
														...button,
														elements: {
															...button.elements,
															url: `${button.elements.url}?returnUrl=${returnUrl}`,
														},
													}}
													className={clsx(
														'mb-2 h-full text-white',
														backgroundBrandColor,
														'!important'
													)}
													globalConfig={globalConfig}
													page={page}
													variant={
														brandColor ?? undefined
													}
												/>
											</div>
										</div>
									))}
									{/* Register Buttons - Full Width */}
									{visibleRegisterButton.map((button, i) => {
										let marginClass = 'ml-1'
										if (visibleLoginButton.length === 0) {
											marginClass =
												i === 0 ? 'mr-1' : 'ml-1'
										}

										return (
											<div
												className={clsx(
													'flex',
													visibleLoginButton.length +
														visibleRegisterButton.length >=
														2
														? 'w-1/2'
														: 'w-fit'
												)}
												key={button.system.id}
											>
												<div
													className={clsx(
														'mt-6 flex flex-1 flex-col justify-center',
														marginClass
													)}
												>
													<ReusableCTAButtonComponent
														BlockMapper={
															BlockMapper
														}
														block={{
															...button,
															elements: {
																...button.elements,
																url: `${button.elements.url}?returnUrl=${returnUrl}`,
															},
														}}
														className="mb-2 h-full"
														globalConfig={
															globalConfig
														}
														page={page}
														variant={
															brandColor ??
															undefined
														}
													/>
												</div>
											</div>
										)
									})}
								</div>
							</div>
						) : null}
					</div>
				</Modal>
			) : null}
		</div>
	)
}
